import { Gender } from '../../types'
import type { AttentionsList, PagerParams } from '../../types'
import type { Amenity, FacilityDetails, FacilityId } from '../facilities/types'
import type { Timetable, TimetableDetails } from '../timetables/types'
import type { Activity } from '../activities/types'
import type { AreaId, Area } from '../areas/types'
import type { FacilityReview, ReviewScores } from '../reviews/types'

export type ProgramId = number

export enum ProgramLevel {
  ENTRY = 1,
  BEGINNER = 2,
  INTERMEDIATE = 3,
  ADVANCED = 4,
}

export interface Belonging {
  wear: boolean
  shoes: boolean
  socks: boolean
  towel: boolean
  drink: boolean
}

export interface Program {
  id: ProgramId
  name: string
  description: string
  level: ProgramLevel
  facility: FacilityDetails
  activities: Activity[]
  belongings: Belonging
  otherBelongings: string[]
  attention: AttentionsList
  minuteAgo: number
  duration: number
  image: string
  sexLimitation: Gender
  minimumPoint: number | null
  minTimetablePoint: number
  maxTimetablePoint: number
}

export interface ProgramTimetable extends Omit<Timetable, 'instructor'> {
  instructor: {
    id: number
    name: string
    facilityId: FacilityId
  }
}

export interface ProgramWithTimetables extends Program {
  timetables: ProgramTimetable[]
}

export interface ProgramDetails extends ProgramWithTimetables {
  reviews: FacilityReview[]
  amenities: Amenity
  reviewScoreAverages: ReviewScores
  reviewCount: number
  facilityReviewCount: FacilityDetails['reviewCount']
  facilityAttention: FacilityDetails['attention']
  facilityImages: FacilityDetails['images']
  hasReadAgreement: boolean
  registeredFavorite: boolean
  isOnline: boolean
}

export interface ProgramTimetableDetails extends TimetableDetails {
  programId: ProgramId
  facilityId: FacilityId
  isOnline: boolean
}

export interface ProgramListParams extends PagerParams {
  activityIds?: number[]
  areaIds?: AreaId[]
  sexLimitation?: Gender[]
  level?: ProgramLevel[]
  searchDates?: string[]
  startTimeBefore?: string
  startTimeAfter?: string
  showFullReservations?: boolean
  isOnline?: boolean
  sortBy?: 'createdAt' | 'minTimetablePoint' | 'minTimetableStartAt'
  order?: 'asc' | 'desc'
}

export interface ProgramSearchResponse {
  activities: Activity[]
  areas: Area[]
}

export type ProgramSearchCountParams = ProgramListParams

export interface ProgramSearchCountResponse {
  count: number
}
